import React from "react";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { Link } from "react-router-dom";

import Section from "../../components/shared-ui/Section/Section";
import Accordion from "../../components/shared-ui/Accordion/Accordion";

import PEDLayout from "./PEDLayout";
import PEDHeroHeader from "../../components/HeroHeader/PEDHeroHeader";

import "../../css/PED/AboutPersistentChronicITP.scss";

export default function AboutPersistentChronicITP() {
  const showPhreesiaModal = (e) => {
    e.preventDefault();
    let modal = document.querySelector("#phreesia-modal");
    document.body.classList.add("no-scroll");
    modal.classList.remove("fadeOut");
    modal.style.display = "block";
    modal.classList.add("fadeIn");
    modal.style.height = "100%";
    modal.querySelector(".exitBtn").addEventListener("click", function() {
      modal.style.display = "none";
      document.body.classList.remove("no-scroll");
    });
    modal
      .querySelector(".modal-overlay")
      .addEventListener("click", function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      });
  };
  return (
    <React.Fragment>
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                url: "https://doptelet.com/pediatric-itp/what-is-itp-in-children/",
                name: "What is Pediatric Immune Thrombocytopenia (ITP)?",
                mainEntity: {
                  "@type": "MedicalCondition",
                  name: "chronic immune thrombocytopenia",
                  alternateName: "ITP",
                  signOrSymptom: [
                    { "@type": "MedicalSymptom", name: "fatigue" },
                    { "@type": "MedicalSymptom", name: "bruising" },
                    { "@type": "MedicalSymptom", name: "Petechiae/Purpura" },
                    { "@type": "MedicalSymptom", name: "ITP Bleeding" },
                  ],
                  possibleTreatment: [
                    { "@type": "DrugClass", name: "TPO-RA" },
                    { "@type": "DrugClass", name: "corticosteroids" },
                    { "@type": "DrugClass", name: "IVIG" },
                    { "@type": "DrugClass", name: "rituximab" },
                  ],
                },
                mainContentOfPage: [
                  "ABOUT PERSISTENT & CHRONIC ITP",
                  "Understanding your child's ITP diagnosis is important. Learn about the causes of this rare platelet disorder in children and explore potential treatment approaches.",
                  "Talk to your child's doctor about what treatment may be right for them. If other therapies aren't working for your child and their platelet counts don't stay lifted, their doctor may consider a thrombopoietin receptor agonist (TPO-RA), such as Doptelet or Doptelet Sprinkle.",
                ],
                lastReviewed: "2025-09-01",
                reviewedBy: {
                  "@type": "Organization",
                  name: "Sobi",
                  url: "https://www.sobi.com/usa/",
                },
              },
            ],
          }),
        ]}
      >
        <title>What is Pediatric Immune Thrombocytopenia (ITP)?</title>
        <meta name="description" content="Learn about this condition that causes low platelet counts and discover information to help you navigate its treatment." />
      </Helmet>
      <PEDLayout pageClass="ped-resources">
        <PEDHeroHeader
          extraClass="large-hero inner-bg home-hero"
          markup={
            <>
              <h1>ABOUT PERSISTENT &amp; CHRONIC ITP</h1>
              <p>
                Understanding your child’s ITP diagnosis is important. Learn
                about the causes of this rare platelet disorder in children and
                explore potential treatment approaches.
              </p>
            </>
          }
          maxWidth="525"
          bgImage={[
            "/images/ped/hero/persistent-hero-desktop.jpg",
            "/images/ped/hero/persistent-hero-desktop.jpg",
          ]}
          bgImageMobile={[
            "/images/ped/hero/persistent-hero.png",
            "/images/ped/hero/persistent-hero@2x.png",
          ]}
        />

        <Section sectionClass="section-padding sign-symptoms">
          <div className="">
            <h2>What are the common signs and symptoms of ITP?</h2>
            <p>
              ITP is known as a heterogeneous disease, which means your child
              may experience different signs and symptoms than someone else with
              ITP.
            </p>
            <p>
              A sign is something that your child’s healthcare team can observe
              or measure, such as their platelet counts. Symptoms are the
              feelings and concerns that your child experiences, such as
              fatigue. Most people with ITP have platelet counts under 100,000
              per microliter, but those counts vary from person to person, and
              your child may find their platelet counts are unpredictable at
              times. The lower their platelet count, the greater the risk of ITP
              symptoms.
            </p>
            <p>
              <strong>Here are some common signs and symptoms of ITP:</strong>
            </p>
          </div>

          <div className="orange-card">
            <div className="orange-card-top">
              <div>
                <div>
                  <img src="/images/ped/icons/icon-fatigue@2x.png" alt="" />
                  <h3>Fatigue</h3>
                </div>
                <p>
                  Your child may feel exhausted and unable to keep up with
                  everyday activities to the point where they may feel tired
                  even after getting a full night’s sleep.
                </p>
              </div>
              <div>
                <div>
                  <img src="/images/ped/icons/icon-bruising@2x.png" alt="" />
                  <h3>Bruising</h3>
                </div>
                <p>
                  Your child may notice their skin start to bruise very easily,
                  even spontaneously, from damaged blood vessels under the skin.
                  Bruising may happen even if they don’t remember injuring
                  themselves.
                </p>
              </div>
            </div>
            <div className="orange-card-bottom">
              <div>
                <div>
                  <img src="/images/ped/icons/icon-pp@2x.png" alt="" />
                  <h3>Petechiae/Purpura</h3>
                </div>
                <p>
                  Your child may see small red or purple spots start to appear
                  from broken blood vessels on areas like their upper arms or
                  lower legs.
                </p>
              </div>
              <div>
                <div>
                  <img src="/images/ped/icons/icon-bleeding@2x.png" alt="" />
                  <h3>ITP Bleeding</h3>
                </div>
                <p>
                  Your child may experience bleeding from their gums and nose,
                  have an unusually heavy menstrual flow, or notice cuts
                  bleeding for longer periods of time. They may also see blood
                  in their urine, vomit, or stool.
                </p>
              </div>
            </div>
          </div>
          <p className="mt-3">
            {" "}
            These are not all the signs and symptoms of ITP. It is important to discuss your signs and symptoms with your doctor as only a doctor can diagnose ITP.
            Doptelet and Doptelet Sprinkle are not approved to treat these
            symptoms.
          </p>
        </Section>

        <Section sectionClass="section-padding treating-persistent">
          <div>
            <h2>How is persistent and chronic ITP treated in children?</h2>
            <p style={{ marginBottom: "30px" }}>
              There are a few options for treating persistent and chronic ITP,
              such as corticosteroids, intravenous immunoglobulin (IVIG),
              rituximab, and TPO-RAs that offer different benefits and risks.
              Click on each of these common treatment options to learn how they
              work.
            </p>
            <Accordion
              title="Corticosteroids"
              id="more-results"
              className="scroll-to-section"
              markup={
                <>
                  <p>
                    Corticosteroids are immunosuppressants that can work by
                    reducing inflammation and the overall activity of your
                    child’s immune system. For ITP, they are commonly prescribed
                    right after diagnosis and are intended for short-term use to
                    stop the body from destroying platelets.
                  </p>
                  <p>
                    While steroids can be effective, continued use, especially
                    at high doses, may have side effects such as increased
                    weight gain, swelling of the face, sleep problems, and mood
                    disturbances such as irritability. Steroids may also come
                    with increased risk of infections, diabetes, cataracts,
                    liver issues, high blood pressure, high blood sugar,
                    osteoporosis, and bone and muscle disorders.
                  </p>
                  <p style={{ color: "#1B4677" }}>
                    For these reasons, the experts who wrote the current
                    American Society of Hematology (ASH) ITP guidelines
                    recommend limiting steroid treatment to less than 6 weeks.
                  </p>
                  <p style={{ color: "#1B4677" }}>
                    Ask your child’s doctor for more information about the ASH
                    Guidelines.
                  </p>
                </>
              }
            />
            <Accordion
              title="Intravenous Immunoglobulin (IVIG)"
              id="more-results"
              className="scroll-to-section"
              markup={
                <>
                  <p>
                    IVIG is an immune-modulating therapy made of antibodies that
                    can help regulate your child’s immune system. IVIG is a
                    first-line treatment typically used in emergency, short-term
                    situations when platelet counts are severely low. IVIG can
                    work by reducing certain immune responses that may be
                    responsible for destroying platelets, but unlike
                    immunosuppressants, it does not weaken the entire immune
                    system.
                  </p>
                </>
              }
            />
            <Accordion
              title="Rituximab"
              id="more-results"
              className="scroll-to-section"
              markup={
                <>
                  <p>
                    Rituximab is an immunosuppressant. Like steroids, it can
                    work to reduce the overall activity of your child’s immune
                    system to stop their body from destroying platelets. Because
                    rituximab changes how the immune system works, it may be
                    easier for your child to get sick or develop other
                    conditions.
                  </p>
                </>
              }
            />
          </div>
          <div className="current-treatment-cta">
            <div>
              <h2>
                Talk to your child’s doctor about what treatment may be right for them.
                If other therapies aren’t working for your child and their
                platelet counts don’t stay lifted, their doctor may consider a
                thrombopoietin receptor agonist (TPO-RA), such as Doptelet or
                Doptelet Sprinkle.
              </h2>
            </div>
            <div>
              <img src="/images/ped/icons/icon-ballon@2x.png" alt="" />
            </div>
          </div>
        </Section>

        <Section sectionClass="section-padding treat-with-doptelet">
          <div>
            <h2>
              Treating with Doptelet and Doptelet Sprinkle, medicines called
              TPO-RAs
            </h2>
            <p>
              Doptelet and Doptelet Sprinkle are TPO-RAs, which are a type of
              medication that mimics the body’s natural process of creating
              platelets. Doptelet and Doptelet Sprinkle can help boost the
              pathway responsible for making platelets, which adds to the
              platelets your child’s body is already making. This ultimately can
              increase their overall count. Doptelet tablets and Doptelet Sprinkle are not used to make platelet counts normal.
            </p>
            <p>
              Neither Doptelet nor Doptelet Sprinkle requires weekly doctor’s
              appointments for treatment injections or becomes less effective
              when your child eats certain foods, like ice cream. Doptelet as a
              tablet must be taken with food. Doptelet Sprinkle oral granules must be mixed
              with a soft food or liquid like applesauce or orange juice. See
              the{" "}
              <Link
                to={{
                  pathname: "/themes/pdf/Instructions-for-Use.pdf"
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Instructions for Use</span>
              </Link>{" "}
              for a full list of recommended soft foods and liquids. Work with
              the doctor to determine a dosing schedule that works best for you
              and your child. The doctor may also adjust their dose based on
              other medications your child takes and their platelet counts.
            </p>
            <p>
              Your child’s doctor will check their platelet count before, during, and for at least 4 weeks
              after stopping their treatment with Doptelet or Doptelet Sprinkle.
            </p>
          </div>
        </Section>

        <Section sectionClass="section-padding">
          <div className="presistent-cta">
            <div>
              <img src="/images/ped/icons/icon-soft-serve@2x.png" alt="" />
            </div>
            <div>
              <h2>Find a persistent or chronic ITP specialist near you</h2>
              <p>
                One of the most important steps to getting the care your child
                deserves is finding the right provider in your area.
              </p>
              <a
                href="#"
                className="btn btn-primary"
                onClick={showPhreesiaModal}
              >
                Find a specialist
              </a>
              <p>
                Sobi is not responsible for the choices or actions that you or
                others take because of information provided by this free tool.
              </p>
            </div>
          </div>
        </Section>
      </PEDLayout>
    </React.Fragment>
  );
}
